<template lang="pug">
	.station-owner-invoices-list.flex-col.space.tb-padding
		ListView.full-width(ref="listView", initialView="table", :hideViewsSelection="true", :getTotal="(r) => r.TotalNumberOfRecords", :getItems="(r) => prepareData(r.Results)",
			:getData="$Data.getInvoiceUploadings", :columns="columns",
			:columnHidingEnabled="false"
		)
			template(v-slot:listing-cell-template="{ data }")
				DxButton(icon="close", @click="removeInvoice(data.UploadingId)")
			template(v-slot:owner-state-template="{ data }")
				.flex-row.space(v-if="data.UploadErrors")
					.button.error(@click="showErrorsFor = data") Errors ({{getErrorsList(data.UploadErrors).length}})
					.button.action.flex-row.align-center.space(@click="() => reupload(data)")
						i.fas.fa-upload
						span {{$tCaption('Reupload')}}
				span(v-else) {{data.OwnerStatusName && data.OwnerStatusName()}}
			template(v-slot:agency-template="{ data }")
				.flex-row.align-center.space
					InvoicesLog(:uploadingId="data.UploadingId")
					template(v-if="!data.AgencyId")
						.flex-row(v-if="!specifyAgencyFor || specifyAgencyFor.UploadingId !== data.UploadingId")
							.button.error(@click="specifyAgencyFor = data") {{$tCaption('Agency not found')}}
						CompanySelector(v-else, :companyType="2", @select-data="agency => onAgencySelect(data, agency)")
					.unlinked(v-else-if="(data.NotLinkedCompanies || []).filter(f => f.CompanyType === 2).length",
						@click="() => setCompanylinks((data.NotLinkedCompanies || []).filter(f => f.CompanyType === 2)[0])",
						:title="$tCaption('Company is not linked with your account', '@invoices')",
						:data-unlinked-text="$tCaption('Not linked', '@invoices')") {{data.AgencyNameCalculated}}
					span(v-else) {{data.AgencyNameCalculated}}
			template(v-slot:advertiser-template="{ data }")
				.flex-row.align-center.space
					template(v-if="data.AdvertiserName && !data.AdvertiserId")
						.flex-row(v-if="!specifyAdvertiserFor || specifyAdvertiserFor.UploadingId !== data.UploadingId")
							.button.error.flex-row.space.align-center(@click="specifyAdvertiserFor = data")
								i.fas.fa-edit
								span {{$tCaption('Advertiser not found')}}
						CompanySelector(v-else, :companyType="0", @select-data="advertiser => onAdvertiserSelect(data, advertiser)")
					.unlinked(v-else-if="(data.NotLinkedCompanies || []).filter(f => f.CompanyType === 0).length",
						@click="() => setCompanylinks((data.NotLinkedCompanies || []).filter(f => f.CompanyType === 0)[0])",
						:title="$tCaption('Company is not linked with your account', '@invoices')",
						:data-unlinked-text="$tCaption('Not linked', '@invoices')") {{data.AdvertiserNameCalculated}}
					span(v-else) {{data.AdvertiserNameCalculated}}
			template(slot="left-actions")
				.flex-row.space.align-center
					.font-large(v-if="!hideHeader") {{$tCaption('Invoice Upload')}}
					slot(name="header")
					FileUploader(ref="uploader", @upload="onFileUpload", icon="upload", :dropText="$tCaption('or drop EDI files here')", :text="$tCaption('Upload EDI invoices')", :multiple="true", :preventClose="true", :isLoading="isLoading", @popup="uploaderPopup")
						.station-owner-invoices-list.flex-col.space-large
							.result.flex-col.space(v-for="r in uploadingResults", :class="[r.result.Success ? 'success' : 'error']")
								.file-name {{r.name}}
								.error(v-if="r.result.ErrorMessage") {{r.result.ErrorMessage}}
					Loader(v-if="loading", :text="$tCaption('Loading', '@actions')")
		Popup(v-if="showErrorsFor", :popupVisible="!!showErrorsFor", :width="640", :height="480", :title="$tCaption('Errors')", @hiding="showErrorsFor = null")
			.station-owner-invoices-list
				.errors.flex-col.space
					.error.flex-1.nowrap.flex-row(v-for="(error, idx) in getErrorsList(showErrorsFor.UploadErrors)")
						.number.flex-row.align-center {{idx + 1}}
						.text.flex-1.wrap {{error}}
					.error.warning.flex-1.nowrap.flex-row(v-for="(error, idx) in getErrorsList(showErrorsFor.UploadWarnings)")
						.number.flex-row.align-center {{idx + 1 + getErrorsList(showErrorsFor.UploadErrors).length}}
						.flex-col.space
							.text.flex-1.wrap {{error}}
							.flex-row
								DxButton(v-if="showErrorsFor.UploadWarnings", :text="$tCaption('Invite advertiser')", @click="() => inviteAdvertiser()")
			template(slot="actions-right")
				DxButton(:text="$tCaption('Close')", @click="showErrorsFor = null")
		InviteCompany(ref="inviteCompany")
		CompanySelector(ref="companySelector", :dropdownVisible="false")
</template>

<script>
	import DxButton from 'devextreme-vue/button';
	import ListView from 'app-core/ListView.vue';
	import FileUploader from 'app-core/FileUploader.vue';
	import Loader from 'app-core/Loader.vue';
	import axios from 'axios';
	import Promise from 'bluebird';
	import Popup from 'app-core/Popup.vue';
	import Dropdown from 'app-core/Dropdown.vue';
	import InvoicesLog from './InvoicesLog.vue';
	import InviteCompany from 'view/company/InviteCompany.vue';
	import CompanySelector from 'view/company/CompanySelector.vue';

	export default {
		translationCategory: 'invoice.UploadingsList',
		name: 'station-owner-invoices-list',
		components: {
			ListView, DxButton, FileUploader, Popup, Dropdown, InvoicesLog, InviteCompany, CompanySelector, Loader
		},
		props: ['hideHeader'],
		data() {
			return {
				columns: [
					{ dataField: 'AgencyNameCalculated', caption: () => this.$tCaption('Agency'), cellTemplate: 'agency-template' },
					{ dataField: 'AdvertiserNameCalculated', caption: () => this.$tCaption('Advertiser'), cellTemplate: 'advertiser-template' },
					{ dataField: 'AgencyStatusName', caption: () => this.$tCaption('Agency status') },
					{ dataField: 'NumberOfInvoices', caption: () => this.$tCaption('Number of invoices'), dataType: 'number' },
					{ dataField: 'NumberOfSpots', caption: () => this.$tCaption('Spots'), dataType: 'number' },
					{ dataField: 'TotalDollars', caption: () => this.$tCaption('Total'), dataType: 'number', format: (v) => this.$formatNumber(v) },
					{ dataField: 'CreatedByContact', caption: () => this.$tCaption('Uploaded by') },
					{ dataField: 'Created', caption: () => this.$tCaption('Uploaded date'), dataType: 'date' },
					{ dataField: 'OwnerStatusName', caption: () => this.$tCaption('Upload state'), cellTemplate: 'owner-state-template', },
					{ dataField: 'Name', caption: ' ', cellTemplate: 'listing-cell-template', width: 80 }
				],
				uploadingResults: [],
				isLoading: false,
				doRefresh: false,
				agencyStatuses: [() => this.$tCaption('New'), () => this.$tCaption('Viewed'), () => this.$tCaption('Downloaded')],
				ownerStatuses: [() => this.$tCaption('New'), () => this.$tCaption('Pending validation'), () => this.$tCaption('Validated')],
				showErrorsFor: null,
				specifyAgencyFor: null,
				specifyAdvertiserFor: null,
				reuploadFor: null,
				loading: false
			};
		},
		mounted() {
			if (__DEBUG__)
				window.uploadingsList = this;
		},
		methods: {
			onAdvertiserSelect(invoice, advertiser) {
				if (__DEBUG__)
					console.debug('On advertiser select', { invoice, advertiser });
				this.specifyAdvertiserFor = null;
				this.$Data.getExternalApi('ElectronicInvoicing/UpdateInvoiceUploadingAdvertiser',`uploadingId=${invoice.UploadingId}&advertiserId=${advertiser.Oid}`).then(r => {
					this.$refs.listView.reload();
				});
			},
			setCompanylinks(item) {
				this.loading = true;
				this.$refs.companySelector.processSelect(item, true).then(r => this.$refs.listView.reload()).finally(() => this.loading = false);
			},
			reupload(data) {
				this.reuploadFor = data;
				this.$refs.uploader.showPopup();
			},
			onAgencySelect(uploading, agency) {
				if (__DEBUG__)
					console.debug('On agency select', { uploading, agency });
				this.specifyAgencyFor = null;
				this.$Data.getExternalApi('ElectronicInvoicing/UpdateInvoiceUploadingAgency',`uploadingId=${uploading.UploadingId}&agencyId=${agency.Oid}&agencyName=${agency.Name}`).then(r => {
					this.$refs.listView.reload();
				});
			},
			getErrorsList(errors) {
				return `${errors}`.split('\r\n');
			},
			prepareData(data) {
				return (data || []).map(f => ({
					...f,
					AgencyNameCalculated: f.AgencyName || f.AgencyId,
					AdvertiserNameCalculated: f.AdvertiserName || f.AdvertiserCode,
					OwnerStatusName: this.ownerStatuses[f.OwnerStatus],
					AgencyStatusName: this.agencyStatuses[f.AgencyStatus],
				}));
			},
			uploaderPopup(v) {
				if (v) {
					this.uploadingResults = [];
					this.doRefresh = false;
				} else {
					if (this.doRefresh)
						this.$refs.listView.reload();
					this.reuploadFor = null;
				}
			},
			removeInvoice(uploadingId) {
				this.$confirmMessage({
					title: this.$tCaption('Confirm action', '@actions'),
					text: this.$tCaption('Are you sure you want to remove invoices?'),
					handler: () => {
						this.$Data.getExternalApi('ElectronicInvoicing/DeleteInvoiceUploading', `uploadingId=${uploadingId}`).then(r => {
							if (r.Success) {
								this.$notifySuccess(this.$tCaption('Invoice has been removed'));
								this.$refs.listView.reload();
							} else {
								this.$notifyError(r.ErrorMessage);
							}
						}).finally(() => this.$refs.listView.reload());
					}
				});
			},
			onFileUpload(v) {
				if (__DEBUG__)
					console.debug('Upload EDI invoices', v);
				var func = () => {
					var promises = v.map(file => {
						var fileName = file.name;
						var formData = new FormData();
						formData.append('File', file);
						return axios.post(this.$Data.getMethodUrl('postExternalApi?method=ElectronicInvoicing/UploadInvoicesFromEdiFile'), formData, {
							headers: {
								'Content-Type': 'multipart/form-data',
								authToken: this.$auth.accessToken(),
								...__HEADERS__
							}
						}).then(r => {
							return { name: fileName, result: r.data };
						});
					});
					if (promises.length)
					{
						this.isLoading = true;
						Promise.all(promises).then((r) => {
							if (__DEBUG__)
								console.debug('Files uploaded', r);
							this.uploadingResults = [];
							r.map(res => {
								this.uploadingResults.push(res);
							});
							var hasErrors = this.uploadingResults.filter(f => f.result.Success === false).length;
							if (hasErrors)
								this.$notifyError(this.$tCaption('There are some errors in uploaded files'));
							else {
								this.$refs.uploader.close();
								this.$notifySuccess(this.$tCaption('Files processed successfully'));
							}
						})
						.catch(ex => this.$notifyError(ex))
						.finally(() => {
							this.doRefresh = true;
							this.isLoading = false;
						});
					}
				};

				if (this.reuploadFor) {
					this.$confirmMessage({
						title: this.$tCaption('Confirm action', '@actions'),
						text: this.$tCaption('Are you sure you want to replace existing uploading? All linked invoices will be removed.'),
						handler: () => {
							this.$Data.getExternalApi('ElectronicInvoicing/DeleteInvoiceUploading', `uploadingId=${this.reuploadFor.UploadingId}`).then(r => {
								if (r.Success) {
									this.$notifySuccess(this.$tCaption('Invoice has been removed'));
									this.$refs.listView.reload();
								} else {
									this.$notifyError(r.ErrorMessage);
								};
							}).then(() => {
								this.reuploadFor = null;
								func();
							});
						},
						cancel: () => {
							this.reuploadFor = null;
						}
					});
				} else {
					func();
				}
			},
			inviteAdvertiser() {
				this.$refs.inviteCompany.openCompanyInvite({ CompanyType: 0 });
			}
		}
	};
</script>
<style lang='scss'>
	.station-owner-invoices-list {
		.result {
			.file-name {
				background: #93ffbb;
				padding: 2px 8px;
				border-radius: 4px;
			}

			&.error .file-name {
				background: #ff9393;
			}
		}

		.button {
			padding: 6px 18px;
			border-radius: 4px;
			text-align: center;
			text-transform: uppercase;
			font-size: 0.8em;
			cursor: pointer;
			color: #fff;

			&.error {
				background: #ff5b5b;
			}

			&.action {
				background: #50a3ff;
			}
		}

		.errors {
			> .error {
				border-radius: 4px;
				background: #ffd2d2;

				> * {
					padding: 8px;
				}

				.number {
					background: #ff7f7f;
					color: #ffd2d2;
					border-radius: 6px 0px 0px 6px;
				}

				.text {
					word-break: break-word;
				}

				&.warning {
					background: #fffce8;

					.number {
						background: #fff8ca;
						color: rgba(0,0,0,0.5);
					}
				}
			}
		}

		.unlinked {
			color: #ff0000;
			position: relative;

			&:hover {
				cursor: pointer;
				color: #FF9999;
			}

			&:after {
				content: attr(data-unlinked-text);
				position: absolute;
				right: 2px;
				bottom: -16px;
				font-size: 10px;
			}
		}
	}
</style>
